import { format, subDays } from 'date-fns'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { type AgentRunParams, Granularity, useAgentRunFilterOptions } from '@/models/agentRunReport'
import { AgentRunFilters, type AgentRunFilterState } from './AgentRunFilters'
import { AgentsTab } from './AgentsTab'
import { ErrorsTab } from './ErrorsTab'
import { OverviewTab } from './OverviewTab'
import { snapRangeStart } from './period'
import { RunDetailSheet } from './RunDetailSheet'
import { RunsTab } from './RunsTab'

const ISO = (date: Date) => format(date, 'yyyy-MM-dd')

/** A URL param is whatever someone typed, so check it against the enum. */
function parseEnum<T extends Record<string, string>>(
  choices: T,
  value: string | null
): T[keyof T] | undefined {
  return Object.values(choices).includes(value as T[keyof T]) ? (value as T[keyof T]) : undefined
}
const DEFAULT_DAYS = 29

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'agents', label: 'Agents' },
  { value: 'errors', label: 'Errors' },
  { value: 'runs', label: 'Runs' },
]

export function AgentReporting() {
  const [params, setParams] = useSearchParams()
  const [inspectedRun, setInspectedRun] = useState<string | null>(null)

  const granularity = parseEnum(Granularity, params.get('granularity')) ?? Granularity.DAY
  const state: AgentRunFilterState = {
    // Snapped, not stored snapped: switching back to Day restores the picked day.
    start: snapRangeStart(params.get('start') || ISO(subDays(new Date(), DEFAULT_DAYS)), granularity),
    end: params.get('end') || ISO(new Date()),
    granularity,
    agent: params.get('agent') || '',
    model: params.get('model') || '',
    vendor: params.get('vendor') || '',
  }
  // An unknown ?tab= would render the tab strip with no panel under it.
  const tabParam = params.get('tab')
  const tab = TABS.some((item) => item.value === tabParam) ? (tabParam as string) : TABS[0].value

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const onDateRange = (start: string, end: string) => {
    const next = new URLSearchParams(params)
    next.set('start', start)
    next.set('end', end)
    setParams(next, { replace: true })
  }

  const { data: options } = useAgentRunFilterOptions()

  const queryParams: AgentRunParams = {
    start: state.start,
    end: state.end,
    granularity: state.granularity,
    agents: state.agent ? [state.agent] : undefined,
    models: state.model ? [state.model] : undefined,
    vendors: state.vendor ? [state.vendor] : undefined,
  }

  const inspect = (runId: string) => setInspectedRun(runId)

  return (
    <>
      <Helmet>
        <title>Agent Reporting</title>
      </Helmet>
      <div className="flex flex-col gap-6 py-6 md:p-6">
        <div>
          <h1 className="text-2xl font-semibold">Agents</h1>
          <p className="text-ink-500 mt-1 text-sm">
            Every LLM call the product makes — volume, failures, latency, tokens and cost.
          </p>
        </div>

        <AgentRunFilters
          state={state}
          options={options}
          onChange={(key, value) => setParam(key, value)}
          onDateRange={onDateRange}
        />

        <Tabs value={tab} onValueChange={(value) => setParam('tab', value)}>
          <TabsList>
            {TABS.map((item) => (
              <TabsTrigger key={item.value} value={item.value}>
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="mt-6">
            <OverviewTab params={queryParams} />
          </TabsContent>
          <TabsContent value="agents" className="mt-6">
            <AgentsTab params={queryParams} />
          </TabsContent>
          <TabsContent value="errors" className="mt-6">
            <ErrorsTab params={queryParams} onInspectRun={inspect} />
          </TabsContent>
          <TabsContent value="runs" className="mt-6">
            <RunsTab params={queryParams} onInspectRun={inspect} />
          </TabsContent>
        </Tabs>
      </div>

      <RunDetailSheet runId={inspectedRun} onClose={() => setInspectedRun(null)} />
    </>
  )
}
